// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * AGP percentile bucketing — shared by the on-screen AGPChart and the vet PDF
 * report's natively drawn AGP (vic/vetpo#48). Moved out of AGPChart unchanged
 * so the report's percentile bands are the dashboard's, not a second
 * implementation that could drift.
 *
 * Input is an ALREADY-PREPARED series (prepareGlucoseChartSeries: BGM
 * correction + clamp + smoothing) in mmol/L. Bucketing is by local
 * time-of-day in 96 fifteen-minute slots; an empty slot yields NaN so a
 * chart breaks its bands there instead of bridging.
 */

// Minimum valid timestamp: January 1, 2020 (filter out obviously invalid dates like 1973)
const MIN_VALID_TIMESTAMP = new Date('2020-01-01T00:00:00Z').getTime() / 1000

/** Linear-interpolated percentile of `arr` (0 for an empty array). */
export const getPct = (arr: number[], p: number): number => {
  if (arr.length === 0) return 0
  const sorted = [...arr].sort((a, b) => a - b)
  const idx = (sorted.length - 1) * (p / 100)
  const lo = Math.floor(idx)
  const hi = Math.ceil(idx)
  const wt = idx - lo
  if (hi >= sorted.length) return sorted[lo]
  return sorted[lo] * (1 - wt) + sorted[hi] * wt
}

export interface AGPPercentiles {
  /** "00:00", "00:15", … "23:45" — 96 slots. */
  timeSlots: string[]
  p5: number[]
  p25: number[]
  p50: number[]
  p75: number[]
  p95: number[]
}

export function computeAGPPercentiles(
  readings: Array<{ timestamp?: number; glucose: number }>,
): AGPPercentiles {
  const buckets: number[][] = Array.from({ length: 96 }, () => []) // 96 = 24 * 4 (15-min intervals)

  readings.forEach((metric) => {
    if (!metric.timestamp) return
    // Filter out invalid timestamps (before 2020) - catches device clock issues
    if (metric.timestamp < MIN_VALID_TIMESTAMP) return
    const date = new Date(metric.timestamp * 1000)
    const hour = date.getHours()
    const minute = date.getMinutes()
    const bucketIndex = hour * 4 + Math.floor(minute / 15)
    if (bucketIndex >= 0 && bucketIndex < 96) {
      buckets[bucketIndex].push(metric.glucose)
    }
  })

  const timeSlots: string[] = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      timeSlots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`)
    }
  }

  const p5: number[] = []
  const p25: number[] = []
  const p50: number[] = []
  const p75: number[] = []
  const p95: number[] = []

  buckets.forEach((bucket) => {
    if (bucket.length > 0) {
      p5.push(getPct(bucket, 5))
      p25.push(getPct(bucket, 25))
      p50.push(getPct(bucket, 50))
      p75.push(getPct(bucket, 75))
      p95.push(getPct(bucket, 95))
    } else {
      // Fill with NaN for gaps (will create discontinuities in the chart)
      p5.push(NaN)
      p25.push(NaN)
      p50.push(NaN)
      p75.push(NaN)
      p95.push(NaN)
    }
  })

  return { timeSlots, p5, p25, p50, p75, p95 }
}
